'use client';

import { useState } from 'react';

import { useI18n } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';

import { Avatar, Button, Icon, Link } from '@fshr/components';

import MobileNavLinks, { MobileNavigationLink } from '@profiles/components/header/mobile-nav-links';

interface MobileProfileNavigationProps {
	avatar?: string;
	name?: string;
	initials?: string;
	advisorId?: string;
	isFullyAuthenticated?: boolean;
	close?: () => void;
}

export default function PreferredPartnerMobileProfileNavigation({
	avatar,
	name = '',
	initials,
	advisorId,
	close,
	isFullyAuthenticated = false,
}: MobileProfileNavigationProps) {
	const { t, localeLink } = useI18n();
	const [isOpen, setIsOpen] = useState(true);

	const links = [
		{
			text: t('Profile'),
			link: localeLink('/preferredpartner/profile/overview'),
			requiresFullAuthentication: false,
			showActive: (pathname: string) =>
				/\/?preferredpartner\/profile(\/overview)?\/?$/.test(pathname),
		},
		{
			text: t('Manage Account'),
			link: localeLink('/preferredpartner/profile/manage-account/'),
			requiresFullAuthentication: false,
			showActive: (pathname: string) =>
				pathname.includes('/preferredpartner/profile/manage-account'),
		},
		{
			text: t('Upcoming Bookings'),
			link: localeLink('/preferredpartner/profile/upcoming-bookings/'),
			requiresFullAuthentication: false,
			showActive: (pathname: string) =>
				pathname.includes('/preferredpartner/profile/upcoming-bookings'),
		},
	].filter(Boolean) as MobileNavigationLink[];

	return (
		<div className="border-b border-neutral-300 mx-[35px] flex flex-col gap-[35px]">
			<div className="flex flex-row gap-150 items-center">
				<div className="grow-0 shrink--0">
					<Avatar
						avatar={avatar}
						staticImage
						initials={initials}
						className="h-[46px] w-[46px] md:h-[46px] md:w-[46px] text-[20px] md:text-[20px] pt-[5px] pl-[2px]"
					/>
				</div>

				<div className="min-w-0 flex-1">
					<Link
						intent="unstyled"
						href="/preferredpartner/profile/overview/"
						className="font-sans font-bold text-[12px] tracking-[3px] uppercase truncate"
					>
						{name}
					</Link>
					{advisorId ? (
						<p className="text-caption text-text-secondary truncate">
							{t('Advisor ID: {0}', [advisorId])}
						</p>
					) : null}
				</div>

				<Button
					intent="unstyled"
					onClick={() => setIsOpen(!isOpen)}
					className="ml-auto h-200 w-200 text-[20px] flex items-center justify-center"
				>
					<Icon icon={isOpen ? 'less_sign' : 'plus_sign'} className="text-[16px]" />
				</Button>
			</div>

			<MobileNavLinks
				links={links}
				isFullyAuthenticated={isFullyAuthenticated}
				close={close}
				className={cn('-mx-[35px] overflow-hidden grid grid-rows-[0fr] transition-all', {
					'grid-rows-[1fr]': isOpen,
				})}
			/>
		</div>
	);
}
